import {
  Button,
  Flex,
  Heading,
  Icon,
  Input,
  MenuButton,
  MenuItemOption,
  MenuOptionGroup,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import firebase from 'firebase';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Clock, Filter } from 'react-feather';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { Pagination } from '../../../components/Pagination';
import { Menu } from '../../../components/styled-chakra/Menu';
import { useCourses } from '../../../hooks/useCourses';
import { CourseCategory, FirebaseCourseWithUid } from '../../../types/types';
import { AdminCourseItem } from './AdminCourseItem';
import { AdminCourseModalQuickstart } from './AdminCourseModalQuickstart';
import { AdminCoursesModal } from './modal';

interface AdminCoursesTabProps {}

export const AdminCoursesTab: React.FC<AdminCoursesTabProps> = () => {
  const db = firebase.firestore();
  const { courses } = useCourses();

  const [categories] = useCollectionData<CourseCategory>(
    db.collection('courseCategories'),
    { idField: 'uid' }
  );

  const [sorting, setSorting] = useState<{
    type: 'alphabetical' | 'lastModified';
    order: 'asc' | 'desc';
  }>({ type: 'lastModified', order: 'asc' });
  const [searchTermFilter, setSearchTermFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const [activeCourseEdit, setActiveCourseEdit] = useState<
    FirebaseCourseWithUid | undefined
  >(undefined);

  const filteredCourses = useMemo(() => {
    const res =
      courses &&
      courses
        .filter((course) => {
          if (!course.recurrence.isRecurring) {
            return moment(course.start)
              .add(course.duration, 'minutes')
              .isAfter(moment());
          }
          return true;
        })
        .filter((course) =>
          searchTermFilter
            ? course.title
                .toLowerCase()
                .includes(searchTermFilter.toLowerCase()) ||
              course.description
                .toLowerCase()
                .includes(searchTermFilter.toLowerCase())
            : true
        )
        .sort((a, b) => {
          if (
            sorting.type === 'lastModified' &&
            a.lastModified &&
            b.lastModified
          ) {
            if (sorting.order === 'asc') {
              return b.lastModified.seconds - a.lastModified.seconds;
            }
            return a.lastModified.seconds - b.lastModified.seconds;
          }
          if (sorting.type === 'alphabetical') {
            const aTitle = a.title.toLowerCase();
            const bTitle = b.title.toLowerCase();
            if (sorting.order === 'asc') {
              return aTitle < bTitle ? -1 : 1;
            }
            return aTitle > bTitle ? -1 : 1;
          }
          return 0;
        });
    if (res && pageNumber > Math.ceil(res.length / 10)) {
      setPageNumber(1);
    }
    return res;
  }, [searchTermFilter, courses, sorting, pageNumber]);

  if (!courses || !categories)
    return (
      <Flex w="full" justify="center">
        <Spinner />
      </Flex>
    );

  return (
    <>
      <Heading size="md" mb={6}>
        Kurse
      </Heading>
      <AdminCourseModalQuickstart />
      <Button
        borderRadius="xl"
        isFullWidth
        leftIcon={<Icon as={Clock} />}
        onClick={() => {
          setActiveCourseEdit(undefined);
          onOpen();
        }}
      >
        Neuen Kurs planen
      </Button>

      <Pagination
        containerProps={{ mt: 8 }}
        currentPage={pageNumber}
        items={filteredCourses}
        onPageChange={setPageNumber}
      />

      <Flex mt={4}>
        <Input
          focusBorderColor="brand.500"
          placeholder="Titel oder Beschreibung durchsuchen"
          borderRadius="xl"
          onChange={(e) => setSearchTermFilter(e.target.value)}
        />
        <Menu
          menuProps={{
            placement: 'bottom-end',
            gutter: 5,
            closeOnSelect: false,
          }}
          triggerComponent={
            <MenuButton
              flexShrink={0}
              ml={2}
              borderRadius="xl"
              leftIcon={<Icon as={Filter} />}
              as={Button}
            >
              Sortieren
            </MenuButton>
          }
        >
          <MenuOptionGroup
            defaultValue="lastModified"
            title="Bedingung"
            onChange={(v: any) => setSorting({ ...sorting, type: v })}
          >
            <MenuItemOption value="lastModified">
              zuletzt geändert
            </MenuItemOption>
            <MenuItemOption value="alphabetical">alphabetisch</MenuItemOption>
          </MenuOptionGroup>
          <MenuOptionGroup
            defaultValue="asc"
            title="Reihenfolge"
            onChange={(v: any) => setSorting({ ...sorting, order: v })}
          >
            <MenuItemOption icon={<Icon as={ArrowUp} />} value="asc">
              aufsteigend{' '}
              {sorting.type === 'alphabetical' ? '(a-z)' : '(neu-alt)'}
            </MenuItemOption>
            <MenuItemOption icon={<Icon as={ArrowDown} />} value="desc">
              absteigend{' '}
              {sorting.type === 'alphabetical' ? '(z-a)' : '(alt-neu)'}
            </MenuItemOption>
          </MenuOptionGroup>
        </Menu>
      </Flex>
      <Flex direction="column" my={6}>
        {courses.length > 0 ? (
          filteredCourses &&
          filteredCourses
            .slice((pageNumber - 1) * 10, pageNumber * 10)
            .map((course, index) => (
              <AdminCourseItem
                editCourse={() => {
                  setActiveCourseEdit(course);
                  onOpen();
                }}
                key={`${course.title + index}-key`}
                course={course}
                category={
                  categories.find(
                    (trail) => trail.uid === course.courseCategoryId
                  )?.title
                }
              />
            ))
        ) : (
          <Text
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={() => {
              setActiveCourseEdit(undefined);
              onOpen();
            }}
          >
            {searchTermFilter !== ''
              ? 'Keine Kurse gefunden'
              : 'Bis jetzt sind noch keine Kurse angelegt. Du kannst hier einen neuen Kurs anlegen'}
          </Text>
        )}
        <Pagination
          currentPage={pageNumber}
          items={filteredCourses}
          onPageChange={setPageNumber}
        />
      </Flex>
      <AdminCoursesModal
        type={activeCourseEdit ? 'edit' : 'create'}
        isOpen={isOpen}
        onClose={onClose}
        firebaseCourse={activeCourseEdit}
        categories={categories}
      />
    </>
  );
};
